import React from 'react';
import { styled } from 'styletron-react';
import { useImageAnalyzerHealth, AnalyzerStatus } from '../../hooks/useImageAnalyzerHealth';

const STATUS_META: Record<AnalyzerStatus, { color: string; bg: string; label: string }> = {
  running: { color: '#065F46', bg: '#D1FAE5', label: 'Running' },
  degraded: { color: '#92400E', bg: '#FEF3C7', label: 'Degraded' },
  unavailable: { color: '#991B1B', bg: '#FEE2E2', label: 'Unavailable' },
  checking: { color: '#6B7280', bg: '#F3F4F6', label: 'Checking…' },
};

const Badge = styled('div', ({ $bg, $color }: { $bg: string; $color: string }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 12px',
  borderRadius: '16px',
  fontSize: '12px',
  fontWeight: 500,
  backgroundColor: $bg,
  color: $color,
  whiteSpace: 'nowrap' as const,
}));

const Dot = styled('span', ({ $color }: { $color: string }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: $color,
  flexShrink: 0,
}));

// Dashboard badge for the Image Analyzer service (SAM-141). Status comes from
// useImageAnalyzerHealth, which polls the service and raises toasts on
// outage/recovery transitions.
export const ImageAnalyzerStatus: React.FC = () => {
  const { status, checkedAt, detail } = useImageAnalyzerHealth();
  const meta = STATUS_META[status];
  const tooltip = [
    `Image Analyzer: ${meta.label}`,
    checkedAt ? `Last check: ${new Date(checkedAt).toLocaleTimeString()}` : null,
    detail || null,
  ].filter(Boolean).join(' — ');

  return (
    <Badge $bg={meta.bg} $color={meta.color} title={tooltip}>
      <Dot $color={meta.color} />
      Image Analyzer: {meta.label}
    </Badge>
  );
};
